import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import introVideo from '../assets/video/WeAre26.mp4';

export default function WcIntroOverlay({
  open,
  onFinished,
  onSkip,
}) {
  const videoRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMounted(true);
    setVisible(true);
  }, [open]);

  useEffect(() => {
    if (!open || !mounted) return;
    const video = videoRef.current;
    if (!video) return;

    // Try to start instantly; since overlay opens on user click,
    // browsers will usually allow autoplay with sound.
    const playPromise = video.play?.();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // If autoplay is blocked, user can tap (which will skip anyway).
      });
    }
  }, [open, mounted]);

  // Disable scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Stop playback as soon as exit is requested.
  useEffect(() => {
    if (!open || !mounted || visible) return;
    try {
      videoRef.current?.pause?.();
    } catch {
    }
  }, [open, mounted, visible]);

  const requestSkip = () => {
    if (!open) return;
    setVisible(false);
  };

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        setMounted(false);
        if (typeof onFinished === 'function') onFinished();
      }}
    >
      {open && mounted && visible && (
        <motion.div
          className="wc-intro-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          onClick={() => {
            if (typeof onSkip === 'function') onSkip();
            requestSkip();
          }}
          role="button"
          aria-label="Skip intro"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: '#000',
            cursor: 'pointer',
            touchAction: 'manipulation',
          }}
        >
          {/* Video layer (pointer-events none so taps always skip) */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000',
            }}
          >
            {/* Keep native 9:16 framing: no zoom/crop/distortion */}
            <div
              style={{
                height: '100vh',
                maxHeight: '100vh',
                maxWidth: '100vw',
                aspectRatio: '9 / 16',
              }}
            >
              <video
                ref={videoRef}
                src={introVideo}
                autoPlay
                playsInline
                preload="auto"
                controls={false}
                // Intentionally NOT muted: must play with sound.
                onEnded={() => setVisible(false)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>

          {/* Premium dark overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 40% 30%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.82) 100%)',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

